import { useMemo } from 'react';
import { Formik, Form } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { KubernetesSecurityFormFields } from './KubernetesSecurityFormFields';
import { KubernetesSecurityFormValues } from './types';
import { useKubernetesSecurityInitialValues } from './useKubernetesSecurityInitialValues';
import { createKubernetesSecurityValidationSchema } from './kubernetesSecurityValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: KubernetesSecurityFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy;
  /** Template data for pre-filling form in create mode */
  templateData?: Record<string, unknown>;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

/**
 * Kubernetes Security Policy Form Component
 *
 * Used by:
 * - PolicyCreateView (step 2): mode="create" with wizard navigation props
 * - ConfigTab (edit view): mode="edit" without wizard navigation
 */
export function KubernetesSecurityPolicyForm({
  onSubmit,
  mode,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useKubernetesSecurityInitialValues(
    policy,
    templateData
  );
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createKubernetesSecurityValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<KubernetesSecurityFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="security-k8s" />
            )}
            <KubernetesSecurityFormFields />
            {/* Edit mode: footer inside Widget */}
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="security-k8s"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>

        {/* Create mode: footer outside Widget with StickyFooter */}
        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="security-k8s"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}
